import axios from 'axios';
import axiosRetry from 'axios-retry';
import Sentry from '../../api/sentry';
import {
  APPLICATION_REDUCER_TYPES,
  URI_PATHS,
  API_ROUTES,
  ONBOARDING_STEPS,
} from '../../constants';

/* Dispatch Helper Functions */
const setCurrentStep = (step, stepIndex) => ({
  type: APPLICATION_REDUCER_TYPES.SET_CURRENT_STEP,
  payload: {
    step,
    stepIndex,
  },
});

const setOnboardingSteps = (stepOrder, configVersion, configSetWithoutUser) => {
  const nextSteps = stepOrder.reduce((acc, currStep, currStepIndex) => {
    const nextStep = stepOrder[currStepIndex + 1] || ONBOARDING_STEPS.Confirm;

    return { ...acc, [currStep]: nextStep };
  }, {});

  return {
    type: APPLICATION_REDUCER_TYPES.SET_ONBOARDING_STEPS,
    payload: {
      stepOrder,
      nextSteps,
      configVersion,
      configSetWithoutUser
    },
  };
};

/* Actions */
const initiateOnboardingConfig = (payload) => async (dispatch, getState) => {
  const { userId } = payload || {};

  const applicationState = getState().application;
  const { configFetched, configSetWithoutUser } = applicationState;

  // if we already have config in state, we dont need to do this again.
  const userKnownStateUnchanged = (userId && !configSetWithoutUser ) || (!userId && configSetWithoutUser)
  if(configFetched && userKnownStateUnchanged) { return; }

  // create new axios instance here until we refactor axios implementation and keep the retry
  // only on newly created axios instance
  const axiosInstance = axios.create();
  const onNetworkRetry = (retryCount, error) => {
    error.retryAttempt = retryCount;
    Sentry.captureException(error);
  };
  axiosRetry(axiosInstance, {
    retries: 3,
    onRetry: onNetworkRetry,
    retryDelay: axiosRetry.exponentialDelay,
  });

  let onboardingConfig;
  const queryParams = {
    userId,
  };
  try {
    onboardingConfig = await axiosInstance.get(API_ROUTES.GET_ONBOARDING_CONFIG, { params: queryParams });
  } catch (err) {
    err.customMessage = "Unable to fetch onboarding configs. Using default configs.";
    Sentry.captureException(err);
    return;
  }

  const { data: configData } = onboardingConfig;
  const { steps, version } = configData;
  dispatch(setOnboardingSteps(steps, version, !userId));
};

const initiateStepsState = (currentStep) => async (dispatch, getState) => {
  const applicationState = getState().application;
  const { stepOrder } = applicationState;
  const stepIndex = stepOrder.findIndex((step) => step === currentStep);

  dispatch(setCurrentStep(currentStep, stepIndex));
};
const updateToNextStep = (history) => (dispatch, getState) => {
  const applicationState = getState().application;
  const { currentStep, nextSteps, stepOrder } = applicationState;
  const currentPathName = history.location.pathname;

  // send users back to their current step if they happen to navigate back to another step
  if(currentPathName !== URI_PATHS[currentStep]) {
    history.push(URI_PATHS[currentStep]);
    return;
  }

  const updateToStep = nextSteps[currentStep];
  const stepIndex = stepOrder.findIndex((step) => step === updateToStep);

  dispatch(setCurrentStep(updateToStep, stepIndex));

  // Temporary solution to redirect within an action. Long term solution would be to upgrade
  // react-router-dom to latest version and use this. https://reactrouter.com/en/main/fetch/redirect
  history.push(URI_PATHS[updateToStep]);
};

const removePlaidTokenV2 = (payload) => async () => {
  if(!payload)
    throw new Error(`Error in removePlaidToken: Invalid paramters passed`);

  const { removeFromDb, plaidLinkToken } = payload || {};
  localStorage.removeItem("plaid_link_token");

  if(!removeFromDb)
    return;

  try {
    await axios.post("/api/v2/accounts/removeLinkToken", { link_token: plaidLinkToken });
  } catch (err) {
    Sentry.captureException(err);
  }
};

const createPlaidEvent = (payload) => async () => {
  const { error_code, request_id, institution_name, institution_id } = payload;
  try {
    await axios.post("/api/v2/plaid-event", {
      error_code,
      request_id,
      institution_name,
      institution_id,
    });
  } catch (err) {
    Sentry.captureException(err);
  }
};

export {
  initiateOnboardingConfig,
  setCurrentStep,
  updateToNextStep,
  initiateStepsState,
  removePlaidTokenV2,
  createPlaidEvent,
};